/**
 * Constantes centralizadas de permissões por role
 * Use estas constantes em vez de hardcoding roles em cada arquivo
 */

// Roles com acesso total ao sistema (veem TODOS os dados)
export const ROLES_ACESSO_TOTAL = ['master', 'diretor', 'gerente', 'bko', 'qualidade'] as const;

// Roles de gestão (podem ver equipes e supervisionar)
export const ROLES_GESTAO = ['master', 'diretor', 'gerente', 'coordenador', 'supervisor'] as const;

// Roles que podem ACESSAR e EDITAR a página de Configurações (produtos, valores,
// mensagens, status, integrações etc). master/gerente já tinham; supervisor e
// qualidade foram liberados a pedido (acesso total, igual master); bko entrou em
// 06/08 — é quem monta serviço digital novo e preço em Planos & Preços, e ficava
// dependendo do master para cadastrar uma licença (Douglas: "preciso que libere
// isso de configurações... para estes que falei").
// ⚠️ Espelha a função SQL public.pode_configurar_sistema(uuid).
export const ROLES_CONFIGURACOES = ['master', 'gerente', 'supervisor', 'qualidade', 'bko'] as const;

// Helper: verifica se role pode acessar a página de Configurações
export function canAccessConfiguracoes(role: string | undefined | null): boolean {
  return ROLES_CONFIGURACOES.includes(role as any);
}

// Roles que podem GERIR as conexões do WhatsApp Inbox (página "Conexões WhatsApp":
// listar/criar/aposentar conexões + atribuir por consultor/equipe + filas).
// ⛔ Só gerente e master (Douglas 25/09/2026: "eles estavam trocando errado").
// A mesma régua vale no banco: fn_conexao_so_gestao (migration
// 20260925170000). O consultor segue religando a PRÓPRIA linha pelo QR.
export const ROLES_CONEXOES_WHATSAPP = ['master', 'gerente'] as const;

// Helper: verifica se role pode gerir as conexões do WhatsApp Inbox
export function canManageConexoesWhatsapp(role: string | undefined | null): boolean {
  return ROLES_CONEXOES_WHATSAPP.includes(role as any);
}

// Supervisor abre a página Conexões WhatsApp SÓ PARA RECONECTAR as linhas da
// própria equipe (Douglas 02/10/2026): vê a equipe dele, aperta o QR ou manda o
// link de conexão. Não cria, não edita, não liga/desliga, não troca linha.
// O banco já aceita isso: fn_conexao_so_gestao deixa passar o religamento.
export const ROLES_RECONECTAR_CONEXOES_EQUIPE = ['supervisor'] as const;

export function canReconectarConexoesEquipe(role: string | undefined | null): boolean {
  return role === 'supervisor';
}

export function canAbrirConexoesWhatsapp(role: string | undefined | null): boolean {
  return canManageConexoesWhatsapp(role) || canReconectarConexoesEquipe(role);
}

// Roles que podem ver pool de leads disponíveis
export const ROLES_VER_POOL_DISPONIVEL = ['master', 'diretor', 'gerente', 'bko', 'qualidade', 'supervisor', 'coordenador'] as const;

// Roles que podem acessar todos os quadros Kanban
export const ROLES_ACESSO_TODOS_QUADROS = ['master', 'diretor', 'gerente', 'bko', 'qualidade'] as const;

// Helper: verifica se role tem acesso total
export function hasFullAccess(role: string | undefined | null): boolean {
  return ROLES_ACESSO_TOTAL.includes(role as any);
}

// Helper: verifica se role é de gestão
export function isGestaoRole(role: string | undefined | null): boolean {
  return ROLES_GESTAO.includes(role as any);
}

/**
 * Retenção: enxerga e pega o pedido cancelado de QUALQUER consultor.
 * (Douglas 03/08/2026)
 *
 * O CONSULTOR RETENÇÃO existe há tempos com `role = 'consultor'` — e por isso
 * o Dashboard de Cancelados só mostrava os cancelados dele mesmo. Nunca
 * recuperou nada: 0 registros no nome dele.
 *
 * Vale só dentro de Cancelados. Em todo o resto do CRM (meta, comissão,
 * carteira) ele continua sendo consultor, com o alcance de sempre.
 */
export function podeRecuperarQualquerCancelado(profile: { funcao?: string | null } | null | undefined): boolean {
  return profile?.funcao === 'CONSULTOR_RETENCAO';
}

// Helper: verifica se role pode ver pool disponível
export function canViewAvailablePool(role: string | undefined | null): boolean {
  return ROLES_VER_POOL_DISPONIVEL.includes(role as any);
}

// Helper: verifica se role tem acesso a todos os quadros
export function hasAllBoardsAccess(role: string | undefined | null): boolean {
  return ROLES_ACESSO_TODOS_QUADROS.includes(role as any);
}

// Roles que podem ter vendas próprias (criam pedidos em seu nome)
// ⚠️ REGRA: Líderes que também vendem (supervisor, coordenador, gerente) podem ser consultor_id
// em pedidos. Master e diretor permanecem em "Ghost Mode" e não vendem em nome próprio.
export const ROLES_VENDEDORES = ['consultor', 'supervisor', 'coordenador', 'gerente'] as const;

// Helper: verifica se role pode ter vendas próprias
export function canHaveOwnSales(role: string | undefined | null): boolean {
  return ROLES_VENDEDORES.includes(role as any);
}

// Roles que podem ser consultor_id em pedidos
// Líderes que vendem também podem assumir a posição de consultor do pedido.
export const ROLES_PODEM_SER_CONSULTOR = ['consultor', 'supervisor', 'coordenador', 'gerente'] as const;

// Helper: verifica se role pode ser consultor_id de um pedido
export function canBeConsultorPedido(role: string | undefined | null): boolean {
  return ROLES_PODEM_SER_CONSULTOR.includes(role as any);
}

// Roles que podem EXPORTAR planilhas (todos, respeitando hierarquia de dados)
export const ROLES_PODEM_EXPORTAR = [
  'master', 'diretor', 'gerente', 'coordenador', 'supervisor', 
  'bko', 'qualidade', 'atendimento', 'consultor'
] as const;

// Roles que podem IMPORTAR planilhas (mais restrito)
export const ROLES_PODEM_IMPORTAR = [
  'master', 'diretor', 'gerente', 'coordenador', 'supervisor', 
  'bko', 'qualidade'
] as const;

// Helper: verifica se role pode exportar planilhas
export function canExportPlanilha(role: string | undefined | null): boolean {
  return ROLES_PODEM_EXPORTAR.includes(role as any);
}

// Helper: verifica se role pode importar planilhas
export function canImportPlanilha(role: string | undefined | null): boolean {
  return ROLES_PODEM_IMPORTAR.includes(role as any);
}

// Roles que podem gerenciar base de dados e distribuição de leads (Base de Dados + Distribuição)
export const ROLES_GESTAO_LEADS = ['master', 'diretor', 'gerente', 'supervisor', 'coordenador', 'bko', 'qualidade'] as const;

// Helper: verifica se role pode acessar gestão de leads (Base de Dados + Distribuição)
export function canManageLeads(role: string | undefined | null): boolean {
  return ROLES_GESTAO_LEADS.includes(role as any);
}

// Roles que podem transferir pedidos entre consultores
export const ROLES_PODEM_TRANSFERIR = [
  'master', 'diretor', 'gerente', 'coordenador', 'supervisor', 
  'bko', 'qualidade'
] as const;

// Helper: verifica se role pode transferir pedido
export function canTransferPedido(role: string | undefined | null): boolean {
  return ROLES_PODEM_TRANSFERIR.includes(role as any);
}
